/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import messages from './messages';

// Lazy-loaded so the modal bundle is only fetched on interaction, not on initial paint.
const PreferenceModal = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_browser-storage-controls-preference-modal" */ '@atlassian/browser-storage-controls/consent-banner/preference-modal'
	).then((m) => ({
		default: m.PreferenceModal,
	})),
);

type CookiePreferencesButtonProps = {
	appearance?: 'default' | 'subtle';
	isInClassicHelpCenter?: boolean;
	spacing?: 'default' | 'compact';
};

const styles = cssMap({
	classicHelpCenter: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		// Match the sibling "Powered by" link, which uses this same token + legacy fallback.
		color: token('color.text.subtlest', '#5E6C84'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		// Nudge up 1px: the sibling "Powered by" link's 20px logo SVG raises its text baseline, so this button reads ~1px lower without it.
		transform: 'translateY(-1px)',
		cursor: 'pointer',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
});

export const CookiePreferencesButton = ({
	appearance = 'subtle',
	spacing = 'compact',
	isInClassicHelpCenter = false,
}: CookiePreferencesButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);
	const label = formatMessage(messages.cookiePreferences);

	return (
		<React.Fragment>
			{isInClassicHelpCenter ? (
				<Pressable
					xcss={styles.classicHelpCenter}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- xcss fontFamily only accepts ADS tokens; 'inherit' (needed to match the sibling link's system font) is static but has no token, so it must live on the style prop
					style={{ fontFamily: 'inherit' }}
					onClick={handleOpen}
				>
					{label}
				</Pressable>
			) : (
				<Button appearance={appearance} spacing={spacing} onClick={handleOpen}>
					{label}
				</Button>
			)}
			{isOpen && (
				<Suspense fallback={null}>
					<PreferenceModal onClose={handleClose} onSubmit={handleClose} />
				</Suspense>
			)}
		</React.Fragment>
	);
};
